import React from 'react';

import slugify from "slugify";
import {toPlainText} from "@portabletext/react";
import {urlFor} from "@/studio/utils/sanity-utils";
import ContentConcept from "@/components/content/ContentConcept";
import ResponsiveIframe from "@/components/ui/iframe/ResponsiveIframe";

export default function BlockContentRenderer({blocks, styles, refs}) {

  const renderList = (listItems, listItemType) => {
    const ListTag = listItemType === 'number' ? 'ol' : 'ul';
    const listClassName = listItemType === 'letter' ? 'letteredList' : listItemType === 'number' ? 'numberedList' : 'bulletList';
    return (
      <ListTag key={`list-${listItemType}`} className={listClassName}>
        {listItems.map(item => (
          <li key={item._key}>
            {item.children.map((child, index) => renderTextWithMarks(child, item.markDefs))}
          </li>
        ))}
      </ListTag>
    );
  };

  // Utility function to convert markDefs array to an object for quick access
  const createMarkLookup = (markDefs) => {
    return markDefs.reduce((acc, def) => {
      acc[def._key] = def;
      return acc;
    }, {});
  };

  const renderMark = (mark, child, markDef) => {
    switch (markDef._type) {
      case "link":
        const rel = markDef.target === '_blank' ? 'noopener' : undefined;
        return <a key={mark} href={markDef.href} target={markDef.target} rel={rel}>{child.text}</a>;
      case "concept":
        return <ContentConcept key={mark}
                               conceptRef={markDef.concept && markDef.concept.slug}>{child.text}</ContentConcept>;
      default:
        return <span key={mark}>{child.text}</span>;
    }
  };

  const renderTextWithMarks = (child, markDefs) => {
    if (child.marks.length === 0) {
      return <span key={child._key}>{child.text}</span>;
    }

    const markLookup = createMarkLookup(markDefs);
    const elements = child.marks.map(mark => {
      const markDef = markLookup[mark];
      return markDef ? renderMark(mark, child, markDef) : null;
    }).filter(Boolean);

    ["strong", "em"].forEach(style => {
      if (child.marks.includes(style)) {
        elements.push(React.createElement(style, {key: style}, child.text));
      }
    });

    return elements.length > 1 ? <span key={child._key}>{elements}</span> : elements[0];
  };

  function renderBlock(block, key, slug, styles, refs) {
    switch (block._type) {
      case "image":
        return <div key={key} className={styles.p}>
          <span style={{display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center'}}>
          <img src={urlFor(block.asset._ref).url()} alt={block.alt} className="contentImage"/>
            {block.caption &&
              <div className="caption">{block.caption}</div>}
          </span>
        </div>
          ;
      case "video":
        return <div key={key} className={styles.p_media}><ResponsiveIframe src={block.url}
                                                                           allow="autoplay; encrypted-media"
                                                                           allowFullScreen/>{block.caption &&
          <div className="caption">{block.caption}</div>}</div>;
      default:
        if (/^h\d$/.test(block.style)) {
          const HeaderTag = block.style;
          return <HeaderTag key={key} id={slug}
                            ref={(ref) => (refs[slug] = ref)}>{block.children.map(child => child.text).join("")}</HeaderTag>;
        } else if (block.style === "normal") {
          return <div className={styles.p} key={key}
                      ref={ref => refs[slug] = ref}>{block.children.map((child, i) => renderTextWithMarks(child, block.markDefs))}</div>;
        }
        return null;
    }
  }

  let listItemAccumulator = [];
  let currentListType = null;

  const renderBlocks = blocks.reduce((acc, block, index) => {
    const slug = slugify(toPlainText(block), {lower: true});
    const key = `${slug}-${index}`;

    if (block._type === "block" && block.listItem) {
      // Check if we are starting a new list type
      if (currentListType && currentListType !== block.listItem) {
        // Render the current accumulated list and start a new one
        acc.push(renderList(listItemAccumulator, currentListType));
        listItemAccumulator = [];
      }
      currentListType = block.listItem; // Update the current list type
      listItemAccumulator.push(block);
    } else {
      // When encountering a non-list block, render any accumulated list items
      if (listItemAccumulator.length > 0) {
        acc.push(renderList(listItemAccumulator, currentListType));
        listItemAccumulator = [];
        currentListType = null; // Reset current list type
      }
      // Render the non-list block
      acc.push(renderBlock(block, key, slug, styles, refs));
    }
    return acc;
  }, []);

  // Check if there are leftover list items to render at the end
  if (listItemAccumulator.length > 0) {
    renderBlocks.push(renderList(listItemAccumulator, currentListType));
  }

  return <div>{renderBlocks}</div>;
}
